import {Attribute} from '../../../components/ObjectAttribute';
import {Json} from '../../../components/JsonObjectCard';
import {RequestExample} from '../../../components/RequestExampleCard';
import {RequestDefinitionData} from '../../../components/RequestDefinition';

const searchPicksRequest: RequestExample = {
    title: {
        verb: 'GET',
        endpointUrl: '/api/picks',
    },
    curl:
        `curl --location \\
--request GET '<YOUR SERVER>/api/picks' \\
--header 'Content-Type: application/json' \\
--header 'Authorization: Bearer <TOKEN>'
`,
};

const searchPicksResponse: Json = {
    title: 'RESPONSE',
    json: {
        'totalCount': 2,
        'totalPages': 1,
        'pageNumber': 1,
        'pageSize': 100,
        'results': [
            {
                'id': 101,
                'number': 'SO12345',
                'dateScheduled': '2026-03-20T00:00:00Z',
                'status': 'Entered',
                'type': 'Pick',
                'priority': '3-Normal',
                'username': 'jsmith',
                'orders': [
                    {
                        'number': '12345',
                        'type': 'SO',
                        'carrier': 'UPS',
                        'value': 'John Doe'
                    }
                ]
            },
            {
                'id': 102,
                'number': 'G100',
                'dateScheduled': '2026-03-21T00:00:00Z',
                'status': 'Committed',
                'type': 'Pick',
                'priority': '2-High',
                'username': 'mdavis',
                'orders': [
                    {
                        'number': '67890',
                        'type': 'PO',
                        'carrier': 'FedEx',
                        'value': 'Acme Corp'
                    },
                    {
                        'number': '42:001',
                        'type': 'WO',
                        'value': 'LP100 - Bike License Plate'
                    }
                ]
            }
        ]
    },
};

const searchPicksQueryParameters: Attribute[] = [
    {
        name: 'pageNumber',
        type: 'integer',
        optional: true,
        description: 'The current page of the results.'
    },
    {
        name: 'pageSize',
        type: 'integer',
        optional: true,
        description: 'The number of returned results per page. (Default 100)'
    },
    {
        name: 'pickNumber',
        type: 'string',
        optional: true,
        description: 'Filter by pick number.',
    },
    {
        name: 'orderNumber',
        type: 'string',
        optional: true,
        description: 'Filter by associated order number (SO, PO, TO, or WO).',
    },
    {
        name: 'orderType',
        type: 'string: \'SO\' | \'PO\' | \'TO\' | \'WO\' | \'All\'',
        optional: true,
        description: 'Filter by order type.',
    },
    {
        name: 'orderValue',
        type: 'string',
        optional: true,
        description: 'Filter by order-specific values. Only takes effect when orderType is specified. The meaning depends on the orderType:\n\n• SO: Customer name or ID\n• PO: Vendor name or ID\n• TO: Location Group name or ID (searches both "from" and "to" location groups)\n• WO: BOM (Bill of Materials) number or ID\n\nThe search will match against different database fields depending on the orderType selected.',
    },
    {
        name: 'carrier',
        type: 'string',
        optional: true,
        description: 'Filter by carrier name or ID. Only applies to SO, PO, and TO orders (not WO).',
    },
    {
        name: 'carrierServiceId',
        type: 'integer',
        optional: true,
        description: 'Filter by carrier service ID. Only takes effect if carrier is present.',
    },
    {
        name: 'pickType',
        type: 'string: \'Pick\' | \'Putaway\' | \'Move\' | \'All\'',
        optional: true,
        description: 'Filter by pick type.',
    },
    {
        name: 'pickStatus',
        type: 'string: \'All\' | \'All Open\' | \'Entered\' | \'Started\' | \'Committed\' | \'Finished\' | \'In Progress\'',
        optional: true,
        description: 'Filter by pick status.',
    },
    {
        name: 'priority',
        type: 'string: \'All\' | \'1-Highest\' | \'2-High\' | \'3-Normal\' | \'4-Low\' | \'5-Lowest\'',
        optional: true,
        description: 'Filter by priority.',
    },
    {
        name: 'dateScheduledFrom',
        type: 'string (yyyy-MM-dd)',
        optional: true,
        description: 'Filter by scheduled date range start.',
    },
    {
        name: 'dateScheduledTo',
        type: 'string (yyyy-MM-dd)',
        optional: true,
        description: 'Filter by scheduled date range end.',
    },
    {
        name: 'username',
        type: 'string',
        optional: true,
        description: 'Filter by assigned user name or ID.',
    },
    {
        name: 'locationGroup',
        type: 'string',
        optional: true,
        description: 'Filter by location group name or ID.',
    },
    {
        name: 'partNumber',
        type: 'string',
        optional: true,
        description: 'Filter by part number.',
    },
    {
        name: 'partDescription',
        type: 'string',
        optional: true,
        description: 'Filter by part description.',
    },
    {
        name: 'fulfillable',
        type: 'boolean',
        optional: true,
        description: 'Filter to show only picks that are at least partially fulfillable (have some inventory available).',
    },
    {
        name: 'fulfillableEntirely',
        type: 'boolean',
        optional: true,
        description: 'Filter to show only picks that are entirely fulfillable (have all inventory available).',
    },
    {
        name: 'toBePrinted',
        type: 'boolean',
        optional: true,
        description: 'Filter by print status (true/false/null for all).',
    },
];

const searchPicksAttributes: Attribute[] = [
    {
        name: 'customFields',
        type: 'list',
        optional: true,
        description: 'A list of custom fields to filter by (provided in request body).',
        children: [
            {
                name: 'id',
                type: 'integer',
                description: 'The unique identifier for the custom field.',
            },
            {
                name: 'value',
                type: 'string',
                description: 'The custom field\'s value to filter by.'
            },
        ]
    }
];

const data: RequestDefinitionData = {
    id: 'search-picks',
    title: 'Search for picks',
    description: 'Searches for picks. Requires PICK_VIEW access right. Results are automatically filtered by the user\'s accessible location groups.',
    endpoint: {
        verb: 'GET',
        path: '/picks',
        tooltip: 'Search for picks',
    },
    attributes: searchPicksAttributes,
    requestObject: searchPicksRequest,
    responseObject: searchPicksResponse,
    queryParameters: searchPicksQueryParameters,
}

export default data;
